import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import * as THREE from 'three';
// @ts-ignore
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PLANT_3D_MOCK_DATA } from '../plant3d-mock-data';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { I18nService } from '../../../services/i18n.service';

// 工具：產生唯一ID
function genUniqueId(floor: string, type: string, idx: number): string {
  return `${floor}_${type}_${idx}_${Math.random().toString(36).substr(2, 9)}`;
}

// 新增：化學物質介面
interface ChemicalInfo {
  name: string;
  usage: string;
  dangerLevel: 'low' | 'medium' | 'high';
  quantity: string;
  location?: string;
}

// 新增：物件資訊介面
interface ObjectInfo {
  name: string;
  type: string;
  floor: string;
  status?: string;
  description?: string;
  chemicals?: ChemicalInfo[];
}

// 新增：樓層背景圖片介面
interface FloorBackgroundImage {
  url: string;
  width?: number;
  height?: number;
  position?: [number, number, number];
  rotation?: number;
  opacity?: number;
}

@Component({
  selector: 'app-plant3-dmap',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './plant3-dmap.component.html',
  styleUrl: './plant3-dmap.component.scss'
})
export class Plant3DMapComponent implements OnInit, OnDestroy, OnChanges {
  mode: '2d' | '3d' = '3d';

  @ViewChild('rendererContainer', { static: true }) rendererContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('canvas2d', { static: false }) canvas2d?: ElementRef<HTMLCanvasElement>;
  @Input() data: any;

  scene!: THREE.Scene;
  camera!: THREE.PerspectiveCamera;
  renderer!: THREE.WebGLRenderer;
  controls!: OrbitControls;
  floorGroups: { [floor: string]: THREE.Group } = {};
  floorList: string[] = [];
  currentFloor: string = '';
  animationId: number = 0;
  showLabels: boolean = false;
  showArea: boolean = false;
  showEscape: boolean = false;
  private labelSprites: Map<THREE.Object3D, THREE.Sprite> = new Map();
  private hoveredObject: THREE.Object3D | null = null;

  clickableItems: { name: string, id: string, uniqueId: string }[] = [];
  activeItemId: string | null = null;
  allItems: { name: string, id: string, floor: string, uniqueId: string }[] = [];

  buildingList: string[] = [];
  currentBuilding: string = '';

  // 新增：下拉選單控制屬性
  selectedDeviceId: string = '';
  selectedAction: string = '';
  private selectedDevice: { name: string, id: string, uniqueId: string } | null = null;

  // 新增：物件選擇狀態
  selectedObjectInfo: ObjectInfo | null = null;

  // 新增：多選顯示選項
  displayOptions = [
    { value: 'area', label: '', checked: false },
    { value: 'labels', label: '', checked: false },
    { value: 'escape', label: '', checked: false }
  ];
  dropdownOpen = false;

  constructor(private i18nService: I18nService) {}

  ngOnInit() {
    // 初始化顯示選項的標籤
    this.displayOptions = [
      { value: 'area', label: this.i18nService.translate('plant3dmap.display.area'), checked: false },
      { value: 'labels', label: this.i18nService.translate('plant3dmap.display.labels'), checked: false },
      { value: 'escape', label: this.i18nService.translate('plant3dmap.display.escape'), checked: false }
    ];
    // mock 工廠平面圖資料
    if (!this.data) {
      this.data = PLANT_3D_MOCK_DATA;
    }
    // 取得建築物清單
    if (this.data.buildings) {
      this.buildingList = this.data.buildings.map((b: any) => b.name);
      this.currentBuilding = this.buildingList[0] || '';
    }
    this.initThree();
    if (this.data) {
      this.buildSceneFromData(this.data);
      // 預設切到 3D 正上方檢視
      setTimeout(() => this.onTopView3D(), 0);
    }
    this.animate();
    this.updateClickableItems();
  }

  ngAfterViewInit() {
    if (this.mode === '2d') {
      setTimeout(() => this.render2DMap(), 0);
    }
    this.resizeRendererToContainer(); // 確保 DOM ready 後才 resize

    // 新增：點擊外部關閉下拉選單
    document.addEventListener('click', this.handleDocumentClick);
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.animationId);
    this.controls?.dispose();
    this.renderer?.dispose();
    window.removeEventListener('resize', this.resizeRendererToContainer);
    // 移除事件監聽器
    document.removeEventListener('click', this.handleDocumentClick);
  }

  // 新增：處理文檔點擊事件
  handleDocumentClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.multi-select-dropdown');
    if (!dropdown && this.dropdownOpen) {
      this.dropdownOpen = false;
    }
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && !changes['data'].firstChange) {
      this.clearScene();
      this.buildSceneFromData(this.data);
      // 樓層切換時自動切到 3D 正上方檢視
      setTimeout(() => this.onTopView3D(), 0);
      if (this.mode === '2d') {
        setTimeout(() => this.render2DMap(), 0);
      }
    }
  }

  initThree() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xf0f0f0);

    // 先給一個預設比例
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
    this.camera.position.set(10, 10, 15);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);

    // 不要在這裡 setSize，等 DOM ready 再 resize
    this.rendererContainer.nativeElement.innerHTML = '';
    this.rendererContainer.nativeElement.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 5;
    this.controls.maxDistance = 100;
    this.controls.target.set(5, 1.5, 0);

    // 簡單光源
    const light = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(light);
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.5);
    dirLight.position.set(10, 20, 10);
    this.scene.add(dirLight);

    // 視窗縮放時自動resize
    window.addEventListener('resize', this.resizeRendererToContainer);
  }

  resizeRendererToContainer = () => {
    if (!this.rendererContainer || !this.renderer) return;
    const container = this.rendererContainer.nativeElement;
    const width = container.clientWidth;
    const height = container.clientHeight;
    if (width && height) {
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
  };

  // 修改：切換區域顯示，只控制區域 label 顯示/隱藏
  toggleShowArea() {
    this.showArea = !this.showArea;
    // 控制所有 type: 'area' group 的 labelSprite.visible
    this.labelSprites.forEach((sprite, obj) => {
      if (
        obj.type === 'Group' &&
        (obj as any).userData &&
        (obj as any).userData['isAreaGroup']
      ) {
        sprite.visible = this.showArea;
      }
    });
  }

  // 修改：切換標註顯示（只控制非區域的 label）
  toggleShowLabels() {
    this.showLabels = !this.showLabels;
    this.labelSprites.forEach((sprite, obj) => {
      // 若 obj 是區域 group 則不處理
      if (
        obj.type === 'Group' &&
        (obj as any).userData &&
        (obj as any).userData['isAreaGroup']
      ) {
        // 區域的 label 由 showArea 控制
        return;
      }
      sprite.visible = this.showLabels;
    });
  }

  // 新增：切換逃生路線顯示，只控制 type: 'escape' 的 line/label 顯示/隱藏
  toggleShowEscape() {
    this.showEscape = !this.showEscape;
    Object.values(this.floorGroups).forEach(group => {
      group.children.forEach(child => {
        // 逃生路線是 THREE.Line 或 Sprite，且 userData['type'] === 'escape'
        if (
          (child.type === 'Line' || child.type === 'Sprite') &&
          child.userData &&
          child.userData['type'] === 'escape'
        ) {
          child.visible = this.showEscape;
        }
      });
    });
  }

  buildSceneFromData(data: any) {
    console.log('buildSceneFromData data:', data);
    // 依據 currentBuilding 找到對應 building
    let building = data.buildings
      ? data.buildings.find((b: any) => b.name === this.currentBuilding)
      : null;
    if (!building) return;
    // 下面都用 building.floors

    const floors = building.floors;
    this.floorGroups = {};
    this.floorList = [];
    this.labelSprites.clear();
    this.allItems = [];
    let uniqueIdMap = new Map<any, string>();
    const clickableTypes = ['sensor', 'rack', 'extinguisher', 'machine', 'door', 'area'];
    if (!floors) {
      console.warn('No floors in data');
      return;
    }

    // 新增：建立文字 Sprite 的工具函式
    const createLabelSprite = (text: string, color = '#222', bg = 'rgba(255,255,255,0.8)') => {
      const fontSize = 36;
      const padding = 8;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      ctx.font = `bold ${fontSize}px sans-serif`;
      const textWidth = ctx.measureText(text).width;
      canvas.width = textWidth + padding * 2;
      canvas.height = fontSize + padding * 2;
      // 重新設字型（canvas 會因 resize 重設 context）
      ctx.font = `bold ${fontSize}px sans-serif`;
      // 背景
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // 文字
      ctx.fillStyle = color;
      ctx.textBaseline = 'top';
      ctx.fillText(text, padding, padding);
      // 建立材質與 Sprite
      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
      const sprite = new THREE.Sprite(material);
      // Sprite 大小與 canvas 寬高成比例
      const scale = 0.02; // 調整此值可改變文字在 3D 場景的實際大小
      sprite.scale.set(canvas.width * scale, canvas.height * scale, 1);
      return sprite;
    };

    // 新增：建立背景圖片平面的工具函式
    const createBackgroundPlane = (floorObj: any) => {
      const backgroundConfig = floorObj.backgroundImage as FloorBackgroundImage;
      if (!backgroundConfig || !backgroundConfig.url) {
        return null;
      }

      const loader = new THREE.TextureLoader();
      const texture = loader.load(
        backgroundConfig.url,
        // onLoad callback
        (texture) => {
          console.log(`Background image loaded for ${floorObj.floor}:`, backgroundConfig.url);
          // 設定貼圖包裝模式
          texture.wrapS = THREE.ClampToEdgeWrapping;
          texture.wrapT = THREE.ClampToEdgeWrapping;
          texture.flipY = false; // 避免圖片上下顛倒
        },
        // onProgress callback (optional)
        undefined,
        // onError callback
        (error) => {
          console.error(`Failed to load background image for ${floorObj.floor}:`, error);
        }
      );

      // 設定平面大小 (預設值或使用設定的寬高)
      const width = backgroundConfig.width || 20;
      const height = backgroundConfig.height || 20;

      // 建立平面幾何體
      const geometry = new THREE.PlaneGeometry(width, height);

      // 建立材質，設定透明度
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: backgroundConfig.opacity || 0.8,
        side: THREE.DoubleSide
      });

      // 建立平面網格
      const plane = new THREE.Mesh(geometry, material);

      // 設定位置（預設在地面上）
      const position = backgroundConfig.position || [0, 0.01, 0]; // 稍微抬高避免 z-fighting
      plane.position.set(position[0], position[1], position[2]);

      // 設定旋轉（預設平躺在地面）
      plane.rotation.x = backgroundConfig.rotation !== undefined ? backgroundConfig.rotation : -Math.PI / 2;

      // 設定使用者資料
      plane.userData = {
        type: 'background',
        floor: floorObj.floor,
        backgroundConfig
      };

      console.log(`Created background plane for ${floorObj.floor}:`, { width, height, position, opacity: backgroundConfig.opacity });

      return plane;
    };

    floors.forEach((floorObj: any, i: number) => {
      console.log(`Building floor[${i}]:`, floorObj);
      const group = new THREE.Group();
      group.name = floorObj.floor;

      // 新增：建立背景圖片平面並加入群組（如果有設定的話）
      const backgroundPlane = createBackgroundPlane(floorObj);
      if (backgroundPlane) {
        group.add(backgroundPlane);
        console.log(`Added background plane to floor ${floorObj.floor}`);
      }

      // 處理所有 objects，將 area.children 攤平成同一層
      let flatObjects: any[] = [];
      (floorObj.objects || []).forEach((obj: any) => {
        if (obj.type === 'area' && Array.isArray(obj.children)) {
          flatObjects.push(obj);
          obj.children.forEach((child: any) => {
            // 標記 child 屬於哪個 area
            flatObjects.push({ ...child, _parentAreaName: obj.name || obj.label || '區域' });
          });
        } else {
          flatObjects.push(obj);
        }
      });

      flatObjects.forEach((obj: any, j: number) => {
        // 新增：產生唯一ID並記錄
        const uniqueId = genUniqueId(floorObj.floor, obj.type, j);
        (obj as any).__uniqueId = uniqueId;
        uniqueIdMap.set(obj, uniqueId);
        if (clickableTypes.includes(obj.type)) {
          this.allItems.push({
            name: obj.label || obj.name || `${obj.type}${j + 1}`,
            id: `${floorObj.floor}_${obj.type}_${j}`,
            floor: floorObj.floor,
            uniqueId
          });
        }
        let mesh: THREE.Mesh | null = null;
        let labelSprite: THREE.Sprite | null = null;
        if (obj.type === 'wall') {
          const geo = new THREE.BoxGeometry(...(obj.size || [1, 1, 1]));
          const mat = new THREE.MeshStandardMaterial({ color: 0x444444 });
          mesh = new THREE.Mesh(geo, mat);
          const pos = obj.position || [0, 0, 0];
          mesh.position.set(pos[0], pos[1], pos[2]);
          console.log(`  Wall[${j}] pos:`, pos);
        } else if (obj.type === 'sensor') {
          const geo = new THREE.SphereGeometry(0.3, 32, 32);
          const mat = new THREE.MeshStandardMaterial({ color: obj.color || 'green' });
          mesh = new THREE.Mesh(geo, mat);
          const pos = obj.position || [0, 0, 0];
          mesh.position.set(pos[0], pos[1], pos[2]);
          mesh.userData = { status: obj.status, type: 'sensor' };
          console.log(`  Sensor[${j}] pos:`, pos, 'status:', obj.status);
        } else if (obj.type === 'rack') {
          const geo = new THREE.BoxGeometry(...(obj.size || [1, 1, 1]));
          const mat = new THREE.MeshStandardMaterial({ color: 0x888888 });
          mesh = new THREE.Mesh(geo, mat);
          const pos = obj.position || [0, 0, 0];
          mesh.position.set(pos[0], pos[1], pos[2]);
          console.log(`  Rack[${j}] pos:`, pos);
        } else if (obj.type === 'extinguisher') {
          const geo = new THREE.CylinderGeometry(0.15, 0.15, 0.5, 32);
          const mat = new THREE.MeshStandardMaterial({ color: obj.color || 'red' });
          mesh = new THREE.Mesh(geo, mat);
          const pos = obj.position || [0, 0, 0];
          mesh.position.set(pos[0], pos[1], pos[2]);
          mesh.rotation.x = Math.PI / 2;
          console.log(`  Extinguisher[${j}] pos:`, pos);
        } else if (obj.type === 'pipe') {
          const geo = new THREE.CylinderGeometry(0.1, 0.1, 1, 32);
          const mat = new THREE.MeshStandardMaterial({ color: obj.color || 'gray' });
          mesh = new THREE.Mesh(geo, mat);
          const pos = obj.position || [0, 0, 0];
          mesh.position.set(pos[0], pos[1], pos[2]);
          mesh.rotation.x = Math.PI / 2;
          console.log(`  Pipe[${j}] pos:`, pos);
        } else if (obj.type === 'light') {
          const geo = new THREE.BoxGeometry(...(obj.size || [1, 1, 1]));
          const mat = new THREE.MeshStandardMaterial({ color: obj.color || 'yellow' });
          mesh = new THREE.Mesh(geo, mat);
          const pos = obj.position || [0, 0, 0];
          mesh.position.set(pos[0], pos[1], pos[2]);
          console.log(`  Light[${j}] pos:`, pos);
        } else if (obj.type === 'machine') {
          const geo = new THREE.BoxGeometry(...(obj.size || [1, 1, 1]));
          const mat = new THREE.MeshStandardMaterial({ color: 0x1976d2 }); // 藍色
          mesh = new THREE.Mesh(geo, mat);
          const pos = obj.position || [0, 0, 0];
          mesh.position.set(pos[0], pos[1], pos[2]);
          console.log(`  Machine[${j}] pos:`, pos);
        } else if (obj.type === 'door') {
          // 門：以灰色 box 呈現
          const geo = new THREE.BoxGeometry(...(obj.size || [1, 2, 0.2]));
          const mat = new THREE.MeshStandardMaterial({ color: 0x888888 });
          mesh = new THREE.Mesh(geo, mat);
          const pos = obj.position || [0, 0, 0];
          mesh.position.set(pos[0], pos[1], pos[2]);
          if (obj.rotationY) mesh.rotation.y = obj.rotationY;
        } else if (obj.type === 'escape') {
          // 不用 return，永遠建立，但根據 showEscape 控制 visible
          if (Array.isArray(obj.path) && obj.path.length > 1) {
            const points = obj.path.map((p: number[]) => new THREE.Vector3(p[0], p[1], p[2]));
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const material = new THREE.LineBasicMaterial({ color: 0xff3333, linewidth: 4 });
            const line = new THREE.Line(geometry, material);
            line.userData = { type: 'escape', ...obj };
            line.visible = this.showEscape;
            group.add(line);
            // 逃生路線 label 放在第一個點上方
            if (obj.label) {
              labelSprite = createLabelSprite(obj.label, '#fff', 'rgba(255,0,0,0.7)');
              const first = obj.path[0];
              labelSprite.position.set(first[0], first[1] + 1, first[2]);
              labelSprite.userData = { type: 'escape', ...obj };
              labelSprite.visible = this.showEscape;
              group.add(labelSprite);
            }
          }
          return; // 不需 mesh 處理
        } else if (obj.type === 'area') {
          // 區域 group 牆與 label 應該永遠建立，僅 label 由 showArea 控制顯示
          const areaGroup = new THREE.Group();
          areaGroup.name = obj.name || obj.label || '區域';
          areaGroup.userData = { isAreaGroup: true }; // 標記為區域 group
          // 牆體
          (obj.walls || []).forEach((wall: any) => {
            const geo = new THREE.BoxGeometry(...(wall.size || [1, 1, 1]));
            const mat = new THREE.MeshStandardMaterial({ color: 0x444444 });
            const wallMesh = new THREE.Mesh(geo, mat);
            const pos = wall.position || [0, 0, 0];
            wallMesh.position.set(pos[0], pos[1], pos[2]);
            wallMesh.userData = { type: 'wall', ...wall };
            areaGroup.add(wallMesh);
          });
          // 區域點擊區域（透明 box）
          if (obj.position && obj.walls && obj.walls.length > 0) {
            let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
            obj.walls.forEach((wall: any) => {
              const [x, y, z] = wall.position;
              const [w, h, d] = wall.size;
              minX = Math.min(minX, x - w / 2);
              maxX = Math.max(maxX, x + w / 2);
              minY = Math.min(minY, y - h / 2);
              maxY = Math.max(maxY, y + h / 2);
              minZ = Math.min(minZ, z - d / 2);
              maxZ = Math.max(maxZ, z + d / 2);
            });
            const boxW = maxX - minX;
            const boxH = maxY - minY;
            const boxD = maxZ - minZ;
            const boxGeo = new THREE.BoxGeometry(boxW, boxH, boxD);
            const boxMat = new THREE.MeshBasicMaterial({ color: 0x00ff00, opacity: 0.1, transparent: true, visible: false });
            const boxMesh = new THREE.Mesh(boxGeo, boxMat);
            boxMesh.position.set((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
            boxMesh.userData = { type: 'area', ...obj };
            areaGroup.add(boxMesh);
          }
          // 區域名稱 label
          if (obj.label) {
            labelSprite = createLabelSprite(obj.label, '#fff', 'rgba(0,0,128,0.7)');
            const pos = obj.position || [0, 0, 0];
            labelSprite.position.set(pos[0], pos[1] + 1.5, pos[2]);
            areaGroup.add(labelSprite);
            this.labelSprites.set(areaGroup, labelSprite);
            labelSprite.visible = this.showArea;
          }
          // 區域 group 永遠加入
          group.add(areaGroup);
          return;
        } else {
          console.warn(`  Unknown object type:`, obj);
          return;
        }
        if (mesh) {
          mesh.userData = { type: obj.type, ...obj, uniqueId };
          group.add(mesh);
          // 新增：若有 label 屬性，建立 Sprite 並加到 group
          if (obj.label) {
            labelSprite = createLabelSprite(obj.label, obj.labelColor || '#222', obj.labelBg || 'rgba(255,255,255,0.8)');
            const pos = obj.position || [0, 0, 0];
            const yOffset = (obj.size?.[1] || 1) / 2 + 0.3;
            labelSprite.position.set(pos[0], pos[1] + yOffset, pos[2]);
            group.add(labelSprite);
            this.labelSprites.set(mesh, labelSprite);
            // 單一物件 label 由 showLabels 控制
            labelSprite.visible = this.showLabels;
          }
        }
      });
      this.scene.add(group);
      this.floorGroups[floorObj.floor] = group;
      this.floorList.push(floorObj.floor);
    });

    this.currentFloor = this.floorList[0] || '';
    this.updateFloorVisibility();
    this.setupSensorClick();
    // 新增：hover 顯示 label
    this.setupHoverLabel();
    this.updateClickableItems(); // <--- 確保初始有資料
  }

  clearScene() {
    Object.values(this.floorGroups).forEach(group => {
      this.scene.remove(group);
      group.clear();
    });
    this.floorGroups = {};
    this.floorList = [];
  }

  updateFloorVisibility() {
    Object.entries(this.floorGroups).forEach(([floor, group]) => {
      group.visible = (floor === this.currentFloor);
    });
  }

  onBuildingChange(event: any) {
    // 切換建築物時，重建場景與樓層
    this.buildSceneFromData(this.data);
    setTimeout(() => this.onTopView3D(), 0);
    this.updateClickableItems();
  }

  onFloorChange(event: any) {
    this.updateFloorVisibility();
    // 樓層切換時自動切到 3D 正上方檢視
    setTimeout(() => this.onTopView3D(), 0);
    this.updateClickableItems();
  }

  onModeToggle() {
    this.mode = this.mode === '3d' ? '2d' : '3d';
    if (this.mode === '2d') {
      setTimeout(() => this.render2DMap(), 0);
    }
  }

  onTopView3D() {
    // 找出目前樓層 group
    const group = this.floorGroups[this.currentFloor];
    if (!group) return;

    // 包圍盒
    const box = new THREE.Box3().setFromObject(group);
    const center = new THREE.Vector3();
    box.getCenter(center);
    const size = new THREE.Vector3();
    box.getSize(size);

    // 修正：確保有合理的預設值，即使場景為空
    if (size.length() === 0) {
      center.set(0, 0, 0);
      size.set(10, 5, 10);
    }

    // 計算適當的相機距離
    const maxDim = Math.max(size.x, size.z);
    const distance = maxDim * 0.8; // 增加一些邊距

    // 相機設在正上方，確保完全置中
    this.camera.position.set(center.x, center.y + distance, center.z);
    this.camera.up.set(0, 0, -1); // 讓Z軸朝上
    this.camera.lookAt(center);

    // 自動調整遠近平面
    this.camera.near = 0.1;
    this.camera.far = Math.max(1000, distance * 3);
    this.camera.updateProjectionMatrix();

    // OrbitControls 目標設為場景中心
    this.controls.target.copy(center);
    this.controls.update();
  }

  animate = () => {
    this.animationId = requestAnimationFrame(this.animate);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  };

  render2DMap() {
    if (!this.canvas2d) return;
    const canvas = this.canvas2d.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // 2D 繪製比例與偏移
    const scale = 30; // 1單位 = 30px
    const offsetX = 100;
    const offsetY = 100;
    // 只畫目前樓層
    const floor = (this.data?.floors || []).find((f: any) => f.floor === this.currentFloor);
    if (!floor) return;
    (floor.objects || []).forEach((obj: any) => {
      if (obj.type === 'wall') {
        ctx.fillStyle = '#444';
        const [x, y, z] = obj.position;
        const [w, , d] = obj.size;
        ctx.fillRect(offsetX + x * scale, offsetY + z * scale, w * scale, d * scale);
      } else if (obj.type === 'sensor') {
        ctx.fillStyle = obj.color || 'green';
        const [x, y, z] = obj.position;
        ctx.beginPath();
        ctx.arc(offsetX + x * scale, offsetY + z * scale, 8, 0, Math.PI * 2);
        ctx.fill();
      } else if (obj.type === 'rack') {
        ctx.fillStyle = '#888';
        const [x, y, z] = obj.position;
        const [w, , d] = obj.size;
        ctx.fillRect(offsetX + x * scale, offsetY + z * scale, w * scale, d * scale);
      } else if (obj.type === 'extinguisher') {
        ctx.fillStyle = obj.color || 'red';
        const [x, y, z] = obj.position;
        ctx.beginPath();
        ctx.arc(offsetX + x * scale, offsetY + z * scale, 8, 0, Math.PI * 2);
        ctx.fill();
      } else if (obj.type === 'pipe') {
        ctx.strokeStyle = obj.color || 'gray';
        ctx.lineWidth = 6;
        const [x, y, z] = obj.position;
        ctx.beginPath();
        ctx.moveTo(offsetX + x * scale, offsetY + z * scale);
        ctx.lineTo(offsetX + x * scale, offsetY + (z + 1) * scale);
        ctx.stroke();
      } else if (obj.type === 'light') {
        ctx.fillStyle = obj.color || 'yellow';
        const [x, y, z] = obj.position;
        ctx.beginPath();
        ctx.arc(offsetX + x * scale, offsetY + z * scale, 8, 0, Math.PI * 2);
        ctx.fill();
      }
    });
  }

  setupSensorClick() {
    this.renderer.domElement.onclick = (event: MouseEvent) => {
      const rect = this.renderer.domElement.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      // 設定更小的閾值以提高檢測精確度
      raycaster.params.Points.threshold = 0.1;
      raycaster.params.Line.threshold = 0.1;
      raycaster.setFromCamera(mouse, this.camera);
      const group = this.floorGroups[this.currentFloor];
      if (!group) return;
      const intersects = raycaster.intersectObjects(group.children, true);
      if (intersects.length > 0) {
        const obj = intersects[0].object;
        const type = obj.userData?.['type'];
        const clickableTypes = ['sensor', 'rack', 'extinguisher', 'machine', 'door', 'area'];

        if (clickableTypes.includes(type)) {
          // 生成並顯示物件資訊
          this.selectedObjectInfo = this.generateObjectInfo(obj.userData, type, this.currentFloor);
        }
      }
    };
  }

  // 新增：hover 顯示 label
  setupHoverLabel() {
    const dom = this.renderer.domElement;
    dom.onmousemove = (event: MouseEvent) => {
      if (this.showLabels) return; // 顯示狀態下不處理 hover
      const rect = dom.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      const raycaster = new THREE.Raycaster();
      // 設定更小的閾值以提高檢測精確度
      raycaster.params.Points.threshold = 0.1;
      raycaster.params.Line.threshold = 0.1;
      raycaster.setFromCamera(mouse, this.camera);
      const group = this.floorGroups[this.currentFloor];
      if (!group) return;

      const intersects = raycaster.intersectObjects(group.children, true);
      let found = false;
      let pointerOnClickable = false;
      let hoveredMesh: THREE.Mesh | null = null;

      // 先重置前一個 hover 物件的顏色
      if (this.hoveredObject && this.hoveredObject !== intersects[0]?.object) {
        const prev = this.hoveredObject as THREE.Mesh;
        if (prev.material && prev.userData['_origColor'] !== undefined && (prev.material as THREE.Material).hasOwnProperty('color')) {
          (prev.material as any).color.set(prev.userData['_origColor']);
        }
      }

      if (intersects.length > 0) {
        const obj = intersects[0].object;
        const clickableTypes = ['sensor', 'rack', 'extinguisher', 'machine', 'door', 'area'];

        // 處理可點擊物件的 hover 效果
        if (clickableTypes.includes(obj.userData?.['type']) && (obj as THREE.Mesh).isMesh) {
          dom.style.cursor = 'pointer';
          pointerOnClickable = true;
          hoveredMesh = obj as THREE.Mesh;

          // 儲存原始顏色
          if (hoveredMesh.userData['_origColor'] === undefined && hoveredMesh.material && (hoveredMesh.material as THREE.Material).hasOwnProperty('color')) {
            hoveredMesh.userData['_origColor'] = (hoveredMesh.material as any).color.getHex();
          }

          // 設定橘色高亮效果
          if (hoveredMesh.material && (hoveredMesh.material as THREE.Material).hasOwnProperty('color')) {
            (hoveredMesh.material as any).color.set(0xffa500); // 橘色高亮
          }

          this.hoveredObject = hoveredMesh;
        }

        // label hover 顯示（只針對非區域 label）
        if (this.labelSprites.has(obj)) {
          // 只隱藏非區域 label
          this.labelSprites.forEach((sprite, o) => {
            if (
              !(o.type === 'Group' &&
                (o as any).userData &&
                (o as any).userData['isAreaGroup'])
            ) {
              sprite.visible = false;
            }
          });
          // 只顯示非區域 label
          if (
            !(obj.type === 'Group' &&
              (obj as any).userData &&
              (obj as any).userData['isAreaGroup'])
          ) {
            this.labelSprites.get(obj)!.visible = true;
          }
          this.hoveredObject = obj;
          found = true;
        }
      }

      // 恢復游標樣式
      if (!pointerOnClickable) {
        dom.style.cursor = '';
      }

      // 只恢復非區域 label
      if (!found && this.hoveredObject) {
        this.labelSprites.forEach((sprite, o) => {
          if (
            !(o.type === 'Group' &&
              (o as any).userData &&
              (o as any).userData['isAreaGroup'])
          ) {
            sprite.visible = false;
          }
        });

        // 如果離開的不是可點擊物件，清除 hoveredObject
        const clickableTypes = ['sensor', 'rack', 'extinguisher', 'machine', 'door', 'area'];
        if (!clickableTypes.includes(this.hoveredObject.userData?.['type'])) {
          this.hoveredObject = null;
        }
      }
    };

    dom.onmouseleave = () => {
      dom.style.cursor = '';

      // 恢復 hover 物件的顏色
      if (this.hoveredObject && (this.hoveredObject as THREE.Mesh).material && (this.hoveredObject as THREE.Mesh).userData['_origColor'] !== undefined && ((this.hoveredObject as THREE.Mesh).material as THREE.Material).hasOwnProperty('color')) {
        ((this.hoveredObject as THREE.Mesh).material as any).color.set((this.hoveredObject as THREE.Mesh).userData['_origColor']);
      }

      if (this.showLabels) return;

      // 只恢復非區域 label
      this.labelSprites.forEach((sprite, o) => {
        if (
          !(o.type === 'Group' &&
            (o as any).userData &&
            (o as any).userData['isAreaGroup'])
        ) {
          sprite.visible = false;
        }
      });
      this.hoveredObject = null;
    };
  }

  updateClickableItems() {
    // 根據 currentFloor 過濾可點擊項目
    this.clickableItems = this.allItems.filter(item => item.floor === this.currentFloor);

    console.log("this.clickableItems", this.clickableItems);

    // 若目前 activeItemId 不在此樓層，則取消 active
    if (!this.clickableItems.some(item => item.id === this.activeItemId)) {
      this.activeItemId = null;
    }

    // 重置下拉選單選擇
    if (!this.clickableItems.some(item => item.id === this.selectedDeviceId)) {
      this.selectedDeviceId = '';
      this.selectedDevice = null;
      this.selectedAction = '';
    }
  }

  setActiveItem(item: { name: string, id: string, uniqueId: string }) {
    this.activeItemId = item.id;
    const group = this.floorGroups[this.currentFloor];
    if (group) {
      group.children.forEach(obj => {
        // 需斷言為 Mesh 才有 material 屬性
        if ((obj as THREE.Mesh).material && ((obj as THREE.Mesh).material as any).color && obj.userData) {
          if (obj.userData['_origColor'] !== undefined) {
            ((obj as THREE.Mesh).material as any).color.set(obj.userData['_origColor']);
          }
        }
      });
      // 直接用 uniqueId 找到對應物件
      let foundObj: any = null;
      for (let i = 0; i < group.children.length; i++) {
        const obj = group.children[i];
        if (obj.userData && obj.userData['uniqueId'] === item.uniqueId) {
          foundObj = obj;
          break;
        }
      }
      if (
        foundObj &&
        (foundObj as THREE.Mesh).material &&
        ((foundObj as THREE.Mesh).material as any).color
      ) {
        if (foundObj.userData['_origColor'] === undefined) {
          (foundObj as THREE.Mesh).userData['_origColor'] = ((foundObj as THREE.Mesh).material as any).color.getHex();
        }
        ((foundObj as THREE.Mesh).material as any).color.set(0xffa500); // 橘色
      }
    }
  }

  // 控制元件移動
  moveItem(item: { name: string, id: string, uniqueId: string }, direction: 'up' | 'down' | 'left' | 'right' | 'forward' | 'backward') {
    const group = this.floorGroups[this.currentFloor];
    if (!group) return;
    // 找到對應物件
    const obj = group.children.find(o => o.userData && o.userData['uniqueId'] === item.uniqueId) as THREE.Object3D | undefined;
    if (!obj) return;
    // 移動步長
    const step = 0.2;
    switch (direction) {
      case 'up':
        obj.position.z -= step;
        break;
      case 'down':
        obj.position.z += step;
        break;
      case 'left':
        obj.position.x -= step;
        break;
      case 'right':
        obj.position.x += step;
        break;
      case 'forward':
        obj.position.y += step;
        break;
      case 'backward':
        obj.position.y -= step;
        break;
    }
    // 若有 label sprite，跟著移動
    if (this.labelSprites.has(obj)) {
      const label = this.labelSprites.get(obj)!;
      label.position.x = obj.position.x;
      label.position.z = obj.position.z;
    }
    this.renderer.render(this.scene, this.camera);
  }

  // 控制元件旋轉
  rotateItem(item: { name: string, id: string, uniqueId: string }, direction: 'cw' | 'ccw') {
    const group = this.floorGroups[this.currentFloor];
    if (!group) return;
    // 找到對應物件
    const obj = group.children.find(o => o.userData && o.userData['uniqueId'] === item.uniqueId) as THREE.Object3D | undefined;
    if (!obj) return;
    // 旋轉角度
    const angle = Math.PI / 12; // 15度
    switch (direction) {
      case 'cw':
        obj.rotation.y -= angle;
        break;
      case 'ccw':
        obj.rotation.y += angle;
        break;
    }
    this.renderer.render(this.scene, this.camera);
  }

  alertItem(item: { name: string, id: string, uniqueId: string }) {
    alert(`點擊了：${item.name}`);
  }

  // 新增：下拉選單處理方法
  onDeviceChange() {
    if (this.selectedDeviceId) {
      this.selectedDevice = this.clickableItems.find(item => item.id === this.selectedDeviceId) || null;
      // 設置為活躍項目，用於 3D 場景中的高亮顯示
      if (this.selectedDevice) {
        this.setActiveItem(this.selectedDevice);
      }
    } else {
      this.selectedDevice = null;
      this.activeItemId = null;
    }
    // 重置動作選擇
    this.selectedAction = '';
  }

  onActionChange() {
    // 當動作改變時的處理邏輯
    console.log('Selected action:', this.selectedAction);
  }

  // 新增：針對選中設備的操作方法
  moveSelectedItem(direction: 'up' | 'down' | 'left' | 'right' | 'forward' | 'backward') {
    if (this.selectedDevice) {
      this.moveItem(this.selectedDevice, direction);
    }
  }

  rotateSelectedItem(direction: 'cw' | 'ccw') {
    if (this.selectedDevice) {
      this.rotateItem(this.selectedDevice, direction);
    }
  }

  alertSelectedItem() {
    if (this.selectedDevice) {
      this.alertItem(this.selectedDevice);
    }
  }

  // 新增：處理多選顯示選項
  onDisplayOptionChange(option: any) {
    switch (option.value) {
      case 'area':
        this.showArea = option.checked;
        this.updateAreaDisplay();
        break;
      case 'labels':
        this.showLabels = option.checked;
        this.updateLabelsDisplay();
        break;
      case 'escape':
        this.showEscape = option.checked;
        this.updateEscapeDisplay();
        break;
    }
  }

  private updateAreaDisplay() {
    this.labelSprites.forEach((sprite, obj) => {
      if (
        obj.type === 'Group' &&
        (obj as any).userData &&
        (obj as any).userData['isAreaGroup']
      ) {
        sprite.visible = this.showArea;
      }
    });
  }

  private updateLabelsDisplay() {
    this.labelSprites.forEach((sprite, obj) => {
      if (
        obj.type === 'Group' &&
        (obj as any).userData &&
        (obj as any).userData['isAreaGroup']
      ) {
        return; // 區域的 label 由 showArea 控制
      }
      sprite.visible = this.showLabels;
    });
  }

  private updateEscapeDisplay() {
    Object.values(this.floorGroups).forEach(group => {
      group.children.forEach(child => {
        if (
          (child.type === 'Line' || child.type === 'Sprite') &&
          child.userData &&
          child.userData['type'] === 'escape'
        ) {
          child.visible = this.showEscape;
        }
      });
    });
  }

  // 新增：相機控制方法
  panCamera(direction: 'left' | 'right' | 'up' | 'down') {
    const distance = 1; // 移動距離
    const target = this.controls.target.clone();
    const position = this.camera.position.clone();

    switch (direction) {
      case 'left':
         target.x += distance;
        position.x += distance;
        break;
      case 'right':
       target.x -= distance;
        position.x -= distance;
        break;
      case 'up':
        target.z += distance;
        position.z += distance;
        break;
      case 'down':
        target.z -= distance;
        position.z -= distance;

        break;
    }

    this.camera.position.copy(position);
    this.controls.target.copy(target);
    this.controls.update();
  }

  zoomCamera(direction: 'in' | 'out') {
    const zoomFactor = 0.9; // 縮放因子
    const currentDistance = this.camera.position.distanceTo(this.controls.target);
    let newDistance: number;

    if (direction === 'in') {
      newDistance = currentDistance * zoomFactor;
    } else {
      newDistance = currentDistance / zoomFactor;
    }

    // 限制縮放範圍
    newDistance = Math.max(this.controls.minDistance, Math.min(this.controls.maxDistance, newDistance));

    // 計算新的相機位置
    const direction3D = new THREE.Vector3();
    direction3D.subVectors(this.camera.position, this.controls.target).normalize();
    const newPosition = this.controls.target.clone().add(direction3D.multiplyScalar(newDistance));

    this.camera.position.copy(newPosition);
    this.controls.update();
  }

  // 新增：切換下拉選單顯示
  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  // 新增：清除選擇的物件
  clearSelectedObject() {
    this.selectedObjectInfo = null;
  }

  // 新增：模擬化學物質資料
  private generateMockChemicals(objectType: string): ChemicalInfo[] {
    const chemicalDatabase: { [key: string]: ChemicalInfo[] } = {
      'rack': [
        {
          name: '硫酸',
          usage: '清潔劑製造',
          dangerLevel: 'high',
          quantity: '50L',
          location: 'A區儲存槽'
        },
        {
          name: '氫氧化鈉',
          usage: 'pH調節劑',
          dangerLevel: 'medium',
          quantity: '25kg',
          location: 'B區密封桶'
        }
      ],
      'machine': [
        {
          name: '潤滑油',
          usage: '機械潤滑',
          dangerLevel: 'low',
          quantity: '200L',
          location: '機台內建油箱'
        },
        {
          name: '清潔溶劑',
          usage: '設備清潔',
          dangerLevel: 'medium',
          quantity: '30L',
          location: '旁邊儲存櫃'
        }
      ],
      'extinguisher': [
        {
          name: 'CO2滅火劑',
          usage: '電器火災撲滅',
          dangerLevel: 'low',
          quantity: '5kg',
          location: '滅火器內'
        }
      ],
      'sensor': [
        {
          name: '校正溶液',
          usage: '感測器校正',
          dangerLevel: 'low',
          quantity: '100ml',
          location: '校正瓶'
        }
      ]
    };

    return chemicalDatabase[objectType] || [];
  }

  // 新增：生成物件詳細資訊
  private generateObjectInfo(userData: any, type: string, floor: string): ObjectInfo {
    const chemicals = this.generateMockChemicals(type);

    const descriptions: { [key: string]: string } = {
      'sensor': '環境監測感測器，用於即時監控工廠內的溫度、濕度、氣體濃度等參數。',
      'rack': '化學物質儲存貨架，採用防腐蝕材質製造，配備安全警示標識。',
      'machine': '生產設備機台，定期進行維護保養，確保安全運作。',
      'extinguisher': '消防滅火設備，定期檢查壓力表與藥劑有效期限。',
      'door': '防火安全門，配備自動關閉裝置與煙霧感應器。',
      'area': '工廠作業區域，劃分不同功能區域以確保作業安全。'
    };

    return {
      name: userData?.label || userData?.name || `${type}${Math.floor(Math.random() * 100)}`,
      type: type,
      floor: floor,
      status: userData?.status || (Math.random() > 0.8 ? 'warning' : 'normal'),
      description: descriptions[type] || '工廠設備物件',
      chemicals: chemicals
    };
  }
}

